/**
 * Password must:
 * - Be at least 8 characters long
 * - Contain at least one lowercase letter (a-z)
 * - Contain at least one uppercase letter (A-Z)
 * - Contain at least one digit (0-9)
 * - Contain at least one special character from: ~!@#$%&*_-+=
 */
export const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[~!@#$%&*_\-+=])[A-Za-z\d~!@#$%&*_\-+=]{8,}$/;

export const PASSWORD_ALLOWED_SPECIALS = '~!@#$%&*_-+=';

export interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validates a password against the complexity policy.
 * Returns a list of human-readable error messages (empty if valid).
 */
export function validatePasswordStrength(password: string): PasswordValidationResult {
  const errors: string[] = [];
  const pwd = password ?? '';

  if (pwd.length < 8) {
    errors.push('Password must be at least 8 characters long.');
  }
  if (!/[a-z]/.test(pwd)) {
    errors.push('Password must contain at least one lowercase letter (a-z).');
  }
  if (!/[A-Z]/.test(pwd)) {
    errors.push('Password must contain at least one uppercase letter (A-Z).');
  }
  if (!/\d/.test(pwd)) {
    errors.push('Password must contain at least one number (0-9).');
  }
  if (!/[~!@#$%&*_\-+=]/.test(pwd)) {
    errors.push(
      `Password must contain at least one special character from: ${PASSWORD_ALLOWED_SPECIALS}`
    );
  }
  if (/[^A-Za-z\d~!@#$%&*_\-+=]/.test(pwd)) {
    errors.push(
      `Password may only contain letters, digits, and these specials: ${PASSWORD_ALLOWED_SPECIALS}`
    );
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Convenience: single boolean check.
 */
export function isStrongPassword(password: string): boolean {
  return validatePasswordStrength(password).valid;
}